import { QueryClient, useMutation, useQueryClient, useSuspenseQuery, useQuery } from '@tanstack/react-query'
import { axiosInstance } from '@/api/axios'
import {
  CategoriesResponse,
  Category,
  CategoryDeleteType,
  CategoryPostType,
  CategoryPutType,
  CategoryType,
  GroupedByLineItem,
  OICCategoryDataResponse,
  OICPeriodPostResponse,
  OicPeriodPostType,
  OicPeriodSwitchInputType,
  OICTotalRequestType,
  OICTotalsResponse
} from './types'
import { createQueryKey, invalidateGlobalsQueryKey, OIC_KEY, TOTALS_KEY } from '@/api/query-keys'
import { PeriodTypeQuery, PeriodTypeView } from '@/types'
import { sortByCreateAt } from '@/lib/utils/parsers'

const getOicCategories = async ({
  financialModelId,
  params
}: {
  financialModelId: string
  params: {
    category_type: CategoryType
  }
}) => {
  const response = await axiosInstance.get<CategoriesResponse>(`/financial-models/${financialModelId}/oic-categories/`, {
    params: {
      category_type: params.category_type
    }
  })
  return response.data
}

export const useGetOicCategoriesQuery = ({
  financialModelId,
  params
}: {
  financialModelId: string
  params: {
    category_type: CategoryType
  }
}) =>
  useSuspenseQuery({
    queryKey: createQueryKey({
      baseKey: OIC_KEY,
      keys: [financialModelId, params.category_type]
    }),
    queryFn: () => getOicCategories({ financialModelId, params }),
    select: data => ({
      categories: sortByCreateAt(data.categories)
    })
  })

const createOicCategory = async ({ financialModelId, body }: { financialModelId: string; body: CategoryPostType }) => {
  const response = await axiosInstance.post<Category>(`/financial-models/${financialModelId}/oic-categories/`, body)
  return response.data
}

export const useCreateOicCategoryQuery = ({ financialModelId }: { financialModelId: string }) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (body: CategoryPostType) => createOicCategory({ financialModelId, body }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: createQueryKey({
          baseKey: OIC_KEY,
          keys: [financialModelId]
        })
      })
      queryClient.resetQueries({
        queryKey: createQueryKey({
          baseKey: OIC_KEY,
          keys: [financialModelId, `oic`]
        })
      })
    }
  })
}

const duplicateOicCategories = async ({ financialModelId, body }: { financialModelId: string; body: CategoryPostType[] }) => {
  const promises = body.map(category =>
    createOicCategory({
      financialModelId,
      body: { ...category, name: `${category.name} - Copy` }
    })
  )
  const results = await Promise.all(promises)
  return results
}

export const useDuplicateOicCategoriesQuery = ({ financialModelId }: { financialModelId: string }) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (body: CategoryPostType[]) => duplicateOicCategories({ financialModelId, body }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: createQueryKey({
          baseKey: OIC_KEY,
          keys: [financialModelId]
        })
      })
      queryClient.resetQueries({
        queryKey: createQueryKey({
          baseKey: TOTALS_KEY,
          keys: [financialModelId, `oic`]
        })
      })
    }
  })
}

const updateOicCategory = async ({ financialModelId, body }: { financialModelId: string; body: CategoryPutType }) => {
  const response = await axiosInstance.put<Category>(`/financial-models/${financialModelId}/oic-categories/${body.category_id}/`, body)
  return response.data
}

export const useUpdateOicCategoryQuery = ({ financialModelId }: { financialModelId: string }) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (body: CategoryPutType) => updateOicCategory({ financialModelId, body }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: createQueryKey({
          baseKey: OIC_KEY,
          keys: [financialModelId]
        })
      })
      queryClient.resetQueries({
        queryKey: createQueryKey({
          baseKey: TOTALS_KEY,
          keys: [financialModelId, `oic`]
        })
      })
    }
  })
}

const deleteOicCategory = async ({ financialModelId, body }: { financialModelId: string; body: CategoryDeleteType[] }) => {
  const promises = body.map(category => axiosInstance.delete(`/financial-models/${financialModelId}/oic-categories/${category.category_id}/`))
  const results = await Promise.all(promises)
  return results
}

export const useDeleteOicCategoryQuery = ({ financialModelId }: { financialModelId: string }) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (body: CategoryDeleteType[]) => deleteOicCategory({ financialModelId, body }),
    onSuccess: () => {
      invalidateGlobalsQueryKey([OIC_KEY])
      queryClient.resetQueries({
        queryKey: createQueryKey({
          baseKey: TOTALS_KEY,
          keys: [financialModelId, `oic`]
        })
      })
    }
  })
}

const getOicCategoryData = async ({
  financialModelId,
  oicCategoryId,
  period,
  view
}: {
  financialModelId: string
  oicCategoryId: string
  period: PeriodTypeQuery
  view: PeriodTypeView
}) => {
  const response = await axiosInstance.get<OICCategoryDataResponse>(`/financial-models/${financialModelId}/oic/data-entries/${oicCategoryId}`, {
    params: {
      data_type: period,
      view
    }
  })
  return response.data
}

export const useGetOicCategoryDataSuspenseQuery = ({
  financialModelId,
  oicCategoryId,
  period,
  view
}: {
  financialModelId: string
  oicCategoryId: string
  period: PeriodTypeQuery
  view: PeriodTypeView
}) =>
  useSuspenseQuery({
    queryKey: createQueryKey({
      baseKey: OIC_KEY,
      keys: [financialModelId, oicCategoryId, period, view]
    }),
    queryFn: () => getOicCategoryData({ financialModelId, oicCategoryId, period, view })
  })

export const useGetOicCategoryDataQuery = ({
  financialModelId,
  oicCategoryId,
  period,
  view
}: {
  financialModelId?: string
  oicCategoryId?: string
  period: PeriodTypeQuery
  view: PeriodTypeView
}) =>
  useQuery({
    queryKey: createQueryKey({
      baseKey: OIC_KEY,
      keys: [financialModelId, oicCategoryId, period, view]
    }),
    queryFn: () =>
      getOicCategoryData({
        financialModelId: financialModelId!,
        oicCategoryId: oicCategoryId!,
        period,
        view
      }),
    enabled: Boolean(financialModelId && oicCategoryId)
  })

const postOicPeriod = async ({ financialModelId, body }: { financialModelId: string; body: OicPeriodPostType }) => {
  const response = await axiosInstance.post<OICPeriodPostResponse>(`/financial-models/${financialModelId}/oic/data-entries/`, body)
  return response.data
}

export const getPostOicPeriodMutationArgs = (financialModelId: string, queryClient: QueryClient) => ({
  mutationFn: (body: OicPeriodPostType) => postOicPeriod({ financialModelId, body }),
  onSuccess: (_: unknown, { oic_category_id }: OicPeriodPostType) => {
    queryClient.invalidateQueries({
      queryKey: createQueryKey({
        baseKey: OIC_KEY,
        keys: [financialModelId, oic_category_id]
      })
    })
    queryClient.resetQueries({
      queryKey: createQueryKey({
        baseKey: TOTALS_KEY,
        keys: [financialModelId, `oic`]
      })
    })
  }
})
export const usePostOicPeriodQuery = ({ financialModelId }: { financialModelId: string }) => {
  const queryClient = useQueryClient()
  return useMutation(getPostOicPeriodMutationArgs(financialModelId, queryClient))
}

const switchOicPeriodInput = async ({ financialModelId, body }: { financialModelId: string; body: OicPeriodSwitchInputType }) => {
  const response = await axiosInstance.post<OICPeriodPostResponse>(
    `/financial-models/${financialModelId}/oic/config/${body.column_name}?oic_category_id=${body.oic_category_id}&input_type=${body.input_type}`,
    {
      year: body.year,
      month: body.month
    }
  )
  return response.data
}

export const useSwitchOicPeriodInputQuery = ({ financialModelId }: { financialModelId: string }) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationKey: createQueryKey({
      baseKey: OIC_KEY,
      keys: [`switch-oic-column-type`, financialModelId]
    }),
    mutationFn: (body: OicPeriodSwitchInputType) => switchOicPeriodInput({ financialModelId, body }),
    onSuccess: (_, { oic_category_id }) => {
      queryClient.invalidateQueries({
        queryKey: createQueryKey({
          baseKey: OIC_KEY,
          keys: [financialModelId, oic_category_id]
        })
      })
      queryClient.resetQueries({
        queryKey: createQueryKey({
          baseKey: TOTALS_KEY,
          keys: [financialModelId, `oic`]
        })
      })
    }
  })
}

const getOicTotals = async ({ financialModelId, params }: { financialModelId: string; params: OICTotalRequestType }) => {
  const response = await axiosInstance.get<OICTotalsResponse>(`/financial-models/${financialModelId}/oic/total-results/`, {
    params
  })
  return response.data
}

export const useGetOicTotalsQuery = ({ financialModelId, params }: { financialModelId: string; params: OICTotalRequestType }) =>
  useSuspenseQuery({
    queryKey: createQueryKey({
      baseKey: TOTALS_KEY,
      keys: [financialModelId, `oic`, params.view, params.oic_type]
    }),
    queryFn: () => getOicTotals({ financialModelId, params }),
    select: data => {
      const groupedByLineItems = data.totals.by_line_item.reduce((acc, item) => {
        if (!acc[item.category_type]) {
          acc[item.category_type] = {
            products: [],
            totalsKey: null,
            cxgr: [],
            totalCxgr: {}
          }
        }
        const totalsKey = item.category_type.toLowerCase().split(`_`)[0]
        acc[item.category_type].products.push(item)
        if (item.cxgr) {
          acc[item.category_type].cxgr.push(item.cxgr)
        }
        if ([`extraordinary`, `financial`, `ordinary`].includes(totalsKey)) {
          const key = totalsKey as `extraordinary` | `financial` | `ordinary`
          acc[item.category_type].totalsKey = key

          if (!acc[item.category_type].totalCxgr.forecast || (!acc[item.category_type].totalCxgr.historical && totalsKey !== null)) {
            const payload = {
              historical: data.totals.by_reporting_category.cxgr?.historical[key].value,
              forecast: data.totals.by_reporting_category.cxgr?.forecast[key].value
            }

            acc[item.category_type].totalCxgr = payload
          }
        }

        return acc
      }, {} as GroupedByLineItem)
      return {
        ...data,
        totals: {
          ...data.totals,
          grouped_by_line_item: Object.entries(groupedByLineItems)
        },
        breakdown: data.breakdown
      }
    }
  })
